import { atom } from "jotai";

import { t } from "@lingui/core/macro";

import { type ReplacementInput, absenceApi } from "../services";
import type {
  Absence,
  AbsenceDraft,
  AbsenceStatus,
  ReplacementDraft,
} from "../types";
import {
  DEFAULT_BEGIN_TIME,
  DEFAULT_END_TIME,
  draftFromAbsence,
  isNewAbsence,
  slotDecision,
} from "../utils/draft";
import { dayCount } from "../utils/format";
import { saveOrder } from "../utils/saveOrder";
import { absenceManagerAtom, initialAbsenceManagerState } from "./state";
import type { AbsenceManagerState } from "./state";

const TAB_ORDER: AbsenceStatus[] = ["current", "future", "past"];

let nextTemporaryId = -1;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function statusFor(
  beginDate: string | null,
  endDate: string | null,
): AbsenceStatus {
  const now = today();

  if (endDate && endDate < now) {
    return "past";
  }

  return beginDate && beginDate > now ? "future" : "current";
}

function draftOf(state: AbsenceManagerState, id: number): AbsenceDraft {
  const stored = state.drafts[id];

  if (stored) {
    return stored;
  }

  const absence = state.absences.find((entry) => entry.id === id);

  return absence
    ? draftFromAbsence(absence)
    : {
        typeId: null,
        sameCaseAbsenceId: null,
        beginDate: null,
        endDate: null,
        beginTime: DEFAULT_BEGIN_TIME,
        endTime: DEFAULT_END_TIME,
        comment: "",
        replacements: {},
      };
}

/** The tab worth opening on: the first one that has anything in it. */
function firstFilledTab(absences: Absence[]): AbsenceStatus {
  return (
    TAB_ORDER.find((tab) =>
      absences.some((absence) => absence.status === tab),
    ) ?? "current"
  );
}

export const showAbsenceManagerAtom = atom(
  null,
  (
    _get,
    set,
    employeeId: number,
    employeeName = "",
    employeeAvatarUrl: string | null = null,
  ) => {
    set(absenceManagerAtom, () => ({
      ...initialAbsenceManagerState,
      isVisible: true,
      employeeId,
      employeeName,
      employeeAvatarUrl,
      loading: true,
    }));
  },
);

export const hideAbsenceManagerAtom = atom(null, (_get, set) => {
  set(absenceManagerAtom, (draft) => {
    draft.isVisible = false;
  });
});

export const loadAbsencesAtom = atom(null, async (get, set) => {
  const employeeId = get(absenceManagerAtom).employeeId;

  if (null === employeeId) {
    return;
  }

  set(absenceManagerAtom, (draft) => {
    draft.loading = true;
    draft.error = null;
  });

  try {
    const [absences, types] = await Promise.all([
      absenceApi.fetchAbsences(employeeId),
      absenceApi.fetchAbsenceTypes(),
    ]);

    const tab = firstFilledTab(absences);

    // The year overview holds the pane until an absence is picked, so the
    // dialog opens on what the year looks like rather than on one row of it.
    set(absenceManagerAtom, (draft) => {
      draft.absences = absences;
      draft.types = types;
      draft.tab = tab;
      draft.selectedId = null;
      draft.loading = false;
    });
  } catch (error) {
    set(absenceManagerAtom, (draft) => {
      draft.loading = false;
      draft.error = error instanceof Error ? error.message : String(error);
    });
  }
});

/** Slow enough to be worth its own request: it walks the year day by day. */
export const loadBalanceAtom = atom(null, async (get, set) => {
  const employeeId = get(absenceManagerAtom).employeeId;

  if (null === employeeId) {
    return;
  }

  set(absenceManagerAtom, (draft) => {
    draft.balanceLoading = true;
  });

  try {
    const balance = await absenceApi.fetchHolidayBalance(employeeId);

    set(absenceManagerAtom, (draft) => {
      draft.balance = balance;
      draft.balanceLoading = false;
    });
  } catch {
    // The balance is a detail of the header; the absences below still stand
    set(absenceManagerAtom, (draft) => {
      draft.balanceLoading = false;
    });
  }
});

export const loadSlotsAtom = atom(null, async (get, set, absenceId: number) => {
  const state = get(absenceManagerAtom);
  const employeeId = state.employeeId;

  if (null === employeeId || state.loadingSlots.includes(absenceId)) {
    return;
  }

  const draft = draftOf(state, absenceId);

  if (!draft.beginDate || !draft.endDate) {
    return;
  }

  set(absenceManagerAtom, (state) => {
    state.loadingSlots.push(absenceId);
  });

  try {
    const slots = await absenceApi.fetchReplacementSlots(employeeId, {
      absenceId: isNewAbsence(absenceId) ? undefined : absenceId,
      from: draft.beginDate,
      to: draft.endDate,
    });

    set(absenceManagerAtom, (state) => {
      state.slots[absenceId] = slots;
    });
  } catch (error) {
    set(absenceManagerAtom, (state) => {
      state.error = error instanceof Error ? error.message : String(error);
    });
  } finally {
    set(absenceManagerAtom, (state) => {
      state.loadingSlots = state.loadingSlots.filter((id) => id !== absenceId);
    });
  }
});

/**
 * The hours already validated over the period, so the panel can say they will
 * not be rewritten. Its own request: the reply is a warning, and a failure to
 * read it must not stop the absence from being edited.
 */
export const loadValidatedHoursAtom = atom(
  null,
  async (get, set, absenceId: number) => {
    const state = get(absenceManagerAtom);
    const employeeId = state.employeeId;
    const draft = draftOf(state, absenceId);

    if (null === employeeId || !draft.beginDate || !draft.endDate) {
      return;
    }

    try {
      const notice = await absenceApi.fetchValidatedHours(employeeId, {
        from: draft.beginDate,
        to: draft.endDate,
      });

      const current = draftOf(get(absenceManagerAtom), absenceId);

      // Picking a range fires a request per click, and the answers can land
      // out of order: only the one that still matches the dates on screen
      // describes the period the warning is about.
      if (
        current.beginDate !== draft.beginDate ||
        current.endDate !== draft.endDate
      ) {
        return;
      }

      set(absenceManagerAtom, (state) => {
        state.validatedHours[absenceId] = notice;
      });
    } catch {
      set(absenceManagerAtom, (state) => {
        delete state.validatedHours[absenceId];
      });
    }
  },
);

export const deselectAbsenceAtom = atom(null, (_get, set) => {
  set(absenceManagerAtom, (state) => {
    state.selectedId = null;
  });
});

export const selectAbsenceAtom = atom(
  null,
  async (get, set, absenceId: number) => {
    set(absenceManagerAtom, (state) => {
      state.selectedId = absenceId;
    });

    if (!get(absenceManagerAtom).slots[absenceId]) {
      await Promise.all([
        set(loadSlotsAtom, absenceId),
        set(loadValidatedHoursAtom, absenceId),
      ]);
    }
  },
);

export const setTabAtom = atom(null, async (get, set, tab: AbsenceStatus) => {
  const state = get(absenceManagerAtom);
  const first = state.absences.find((absence) => absence.status === tab);

  set(absenceManagerAtom, (draft) => {
    draft.tab = tab;

    // An empty tab clears the pane beside it rather than leaving the previous
    // absence on display with nothing selected next to it
    if (!first) {
      draft.selectedId = null;
    }
  });

  if (first) {
    await set(selectAbsenceAtom, first.id);
  }
});

export const patchDraftAtom = atom(
  null,
  async (get, set, patch: Partial<AbsenceDraft>) => {
    const state = get(absenceManagerAtom);
    const selectedId = state.selectedId;

    if (null === selectedId) {
      return;
    }

    const before = draftOf(state, selectedId);
    const after = { ...before, ...patch };

    set(absenceManagerAtom, (draft) => {
      draft.drafts[selectedId] = after;

      const absence = draft.absences.find((entry) => entry.id === selectedId);

      // The list reads the row from the absence, so it has to follow along
      if (absence) {
        absence.beginDate = after.beginDate;
        absence.endDate = after.endDate;
        absence.dayCount = dayCount(after.beginDate, after.endDate);
        absence.typeId = after.typeId;
        absence.typeLabel =
          draft.types.find((type) => type.id === after.typeId)?.label ?? null;
        absence.sameCaseAbsenceId = after.sameCaseAbsenceId;
        absence.status = statusFor(after.beginDate, after.endDate);
      }
    });

    // New dates mean different jobs to cover, and other hours already validated
    if (undefined !== patch.beginDate || undefined !== patch.endDate) {
      await Promise.all([
        set(loadSlotsAtom, selectedId),
        set(loadValidatedHoursAtom, selectedId),
      ]);
    }
  },
);

export const patchReplacementAtom = atom(
  null,
  (get, set, replacedJobId: number, patch: Partial<ReplacementDraft>) => {
    const state = get(absenceManagerAtom);
    const selectedId = state.selectedId;

    if (null === selectedId) {
      return;
    }

    const slots = state.slots[selectedId] ?? [];
    const slot = slots.find((entry) => entry.replacedJobId === replacedJobId);

    if (!slot) {
      return;
    }

    const before = draftOf(state, selectedId);
    const decision = slotDecision(slot, before);

    set(absenceManagerAtom, (draft) => {
      draft.drafts[selectedId] = {
        ...before,
        replacements: {
          ...before.replacements,
          [replacedJobId]: { ...decision, ...patch },
        },
      };
    });
  },
);

/** The "assign to all" control: one choice laid over every job at once. */
export const assignAllAtom = atom(
  null,
  (
    get,
    set,
    decision: Omit<ReplacementDraft, "justification"> & {
      /** Laid over every job too, unless left blank */
      justification?: string;
    },
  ) => {
    const state = get(absenceManagerAtom);
    const selectedId = state.selectedId;

    if (null === selectedId) {
      return;
    }

    const slots = state.slots[selectedId] ?? [];
    const before = draftOf(state, selectedId);
    const replacements = { ...before.replacements };
    const { justification, ...choice } = decision;

    for (const slot of slots) {
      replacements[slot.replacedJobId] = {
        ...slotDecision(slot, before),
        ...choice,
        ...(justification ? { justification } : {}),
      };
    }

    set(absenceManagerAtom, (draft) => {
      draft.drafts[selectedId] = { ...before, replacements };
    });
  },
);

function appendAbsence(
  set: (
    atom: typeof absenceManagerAtom,
    update: (draft: AbsenceManagerState) => void,
  ) => void,
  draft: AbsenceDraft,
  employeeId: number,
  typeLabel: string | null,
): number {
  const id = nextTemporaryId--;
  const status = statusFor(draft.beginDate, draft.endDate);

  set(absenceManagerAtom, (state) => {
    state.absences.unshift({
      id,
      employeeId,
      beginDate: draft.beginDate,
      endDate: draft.endDate,
      beginTime: draft.beginTime,
      endTime: draft.endTime,
      status,
      dayCount: dayCount(draft.beginDate, draft.endDate),
      replacementRows: 0,
      replacedRows: 0,
      typeId: draft.typeId,
      typeDescription: null,
      typeLabel,
      sameCaseAbsenceId: draft.sameCaseAbsenceId,
      comment: draft.comment,
    });

    state.drafts[id] = draft;
    state.tab = status;
    state.selectedId = id;
  });

  return id;
}

export const addAbsenceAtom = atom(null, async (get, set) => {
  const state = get(absenceManagerAtom);

  if (null === state.employeeId) {
    return;
  }

  const start = today();
  const type = state.types[0] ?? null;

  const id = appendAbsence(
    set,
    {
      typeId: type?.id ?? null,
      sameCaseAbsenceId: null,
      beginDate: start,
      endDate: start,
      beginTime: DEFAULT_BEGIN_TIME,
      endTime: DEFAULT_END_TIME,
      comment: "",
      replacements: {},
    },
    state.employeeId,
    type?.label ?? null,
  );

  await set(loadSlotsAtom, id);
});

export const duplicateAbsenceAtom = atom(
  null,
  async (get, set, absenceId?: number) => {
    const state = get(absenceManagerAtom);
    const sourceId = absenceId ?? state.selectedId;

    if (
      null === sourceId ||
      undefined === sourceId ||
      null === state.employeeId
    ) {
      return;
    }

    const source = draftOf(state, sourceId);
    const typeLabel =
      state.types.find((type) => type.id === source.typeId)?.label ?? null;

    const id = appendAbsence(
      set,
      { ...source, replacements: {} },
      state.employeeId,
      typeLabel,
    );

    await set(loadSlotsAtom, id);
  },
);

export const removeAbsenceAtom = atom(
  null,
  async (get, set, absenceId?: number) => {
    const state = get(absenceManagerAtom);
    const targetId = absenceId ?? state.selectedId;

    if (null === targetId || undefined === targetId) {
      return;
    }

    if (!isNewAbsence(targetId)) {
      set(absenceManagerAtom, (draft) => {
        draft.saving = true;
        draft.error = null;
      });

      try {
        await absenceApi.deleteAbsence(targetId);
      } catch (error) {
        set(absenceManagerAtom, (draft) => {
          draft.saving = false;
          draft.error = error instanceof Error ? error.message : String(error);
        });

        return;
      }
    }

    set(absenceManagerAtom, (draft) => {
      draft.absences = draft.absences.filter(
        (absence) => absence.id !== targetId,
      );

      delete draft.drafts[targetId];
      delete draft.slots[targetId];
      delete draft.validatedHours[targetId];

      // Only the deleted row loses the selection; removing another one leaves
      // the detail pane where it was.
      if (draft.selectedId === targetId) {
        draft.selectedId =
          draft.absences.find((absence) => absence.status === draft.tab)?.id ??
          null;
      }

      draft.saving = false;
    });
  },
);

function replacementInputs(draft: AbsenceDraft): ReplacementInput[] {
  return Object.entries(draft.replacements).map(
    ([replacedJobId, decision]) => ({
      replacedJobId: Number(replacedJobId),
      kind: decision.kind,
      employeeId: decision.employeeId,
      justification: decision.justification,
    }),
  );
}

/**
 * The stored identifier a link has to be written with: a temporary one is
 * swapped for the identifier the server gave, and a link to an absence that
 * was never created is dropped rather than sent as a number nobody knows.
 */
function resolveLink(
  link: number | null,
  realIds: Map<number, number>,
): number {
  if (null === link) {
    return 0;
  }

  if (!isNewAbsence(link)) {
    return link;
  }

  return realIds.get(link) ?? 0;
}

function cycleMessage(): string {
  return t`These absences are declared as continuations of each other, so none of them can be saved first. Detach one of them from its case.`;
}

export const saveAtom = atom(null, async (get, set) => {
  const state = get(absenceManagerAtom);
  const employeeId = state.employeeId;

  if (null === employeeId) {
    return false;
  }

  set(absenceManagerAtom, (draft) => {
    draft.saving = true;
    draft.error = null;
  });

  const order = saveOrder(state.drafts);

  if (null === order) {
    set(absenceManagerAtom, (draft) => {
      draft.saving = false;
      draft.error = cycleMessage();
    });

    return false;
  }

  try {
    // An absence created in this same edit is named by a temporary identifier
    // until the server answers with the real one.
    const realIds = new Map<number, number>();

    for (const id of order) {
      const draft = state.drafts[id];
      const link = draft.sameCaseAbsenceId;

      const input = {
        typeId: draft.typeId,
        // Null would read as "leave it as stored", so an absence detached
        // from its case says so with the zero the API expects
        sameCaseAbsenceId: resolveLink(link, realIds),
        beginDate: draft.beginDate,
        endDate: draft.endDate,
        beginTime: draft.beginTime,
        endTime: draft.endTime,
        comment: draft.comment,
        replacements: replacementInputs(draft),
      };

      if (isNewAbsence(id)) {
        const created = await absenceApi.createAbsence(employeeId, input);

        realIds.set(id, created.id);
      } else {
        await absenceApi.updateAbsence(id, input);
      }
    }

    set(absenceManagerAtom, (draft) => {
      draft.drafts = {};
      draft.slots = {};
      draft.validatedHours = {};
      draft.saving = false;
    });

    await set(loadAbsencesAtom);

    return true;
  } catch (error) {
    set(absenceManagerAtom, (draft) => {
      draft.saving = false;
      draft.error = error instanceof Error ? error.message : String(error);
    });

    return false;
  }
});
